import * as moment from "moment-timezone";
import { IAuthorizedHours } from "../models/AuthorizedHours.ts";
import { ReporteChequeo, ReporteRow } from "../models/ReporteChequeo.ts";
import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";
import { formatDataForReport } from "./reporteChequeoUtils.ts";
import {
  normalizeRealChecksForDisplay,
  resolveScheduledTimesForDisplay,
} from "./incidentDisplayTimes.ts";
import {
  getReportDateKey,
  operationalDayKeyForVisitRow,
} from "./operationalDayReporteChequeo.ts";
import { parseTimeToMinutes } from "./payrollBranchMeta.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  resolvePayrollPartnerNightScheduleEnabled,
} from "./nightScheduleEligibility.ts";
import {
  dedupePayrollVisitsByPair,
  isUnscheduledPayrollDay,
  resolvePayrollDayRegisteredHours,
  resolveProgramadoDisplayForPayrollDay,
} from "./payrollVariosDisplay.ts";
import type { PayrollVisitHourRow } from "./payrollVisitHours.ts";

export type ChartDetailedDisplayRecord = {
  fecha: string;
  entradaProgramada: string;
  salidaProgramada: string;
  entradaRegistrada: string;
  salidaRegistrada: string;
  salidaRegistradaFecha?: string;
  horasRegistradas?: number;
  horasTrabajadasEnHorario?: number;
  horasExtras?: number;
  visits?: PayrollVisitHourRow[];
};

function appendSeconds(time: string): string {
  if (!time || time === "N/A" || time === "Varios") {
    return time;
  }
  const parts = String(time).split(":");
  if (parts.length >= 3) {
    return time;
  }
  return `${time}:00`;
}

function findScheduleForFecha(
  fecha: string,
  authorizedSchedule?: IAuthorizedHours[]
) {
  const dateKey = moment(fecha, "DD-MM-YYYY", true).isValid()
    ? moment(fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
    : moment.utc(fecha).format("YYYY-MM-DD");

  return authorizedSchedule?.find(
    (schedule) => moment(schedule.exactDate).format("YYYY-MM-DD") === dateKey
  );
}

function branchMetaForRow(
  row: ReporteRow,
  payrollBranchMeta?: Record<number, BranchReportMeta>
): BranchReportMeta {
  const branchId = Number(row.branch_id ?? row.checkIn?.branch_id);
  const base =
    payrollBranchMeta?.[branchId] ?? {
      nightScheduleEnabled: false,
      cutoffMinutes: 3 * 60,
    };
  return effectiveNightMetaForPartner(
    base,
    row.checkIn?.candidate_shift_name
  );
}

/** Con nocturno: visita principal del día operativo (turno de tarde/noche, no madrugada suelta). */
function pickPrimaryNightVisitRow(rows: ReporteRow[]): ReporteRow | null {
  if (rows.length === 0) {
    return null;
  }
  if (rows.length === 1) {
    return rows[0];
  }

  const minEvening = 18 * 60;
  const scored = rows.map((row) => {
    const inTime = row.checkIn!.assistanceCheck_checkTime;
    const outTime = row.checkOut!.assistanceCheck_checkTime;
    const inMin = parseTimeToMinutes(inTime);
    const hours = calcularTiempoTrabajado(inTime, outTime);
    return {
      row,
      hours,
      inMin,
      isEvening: inMin >= minEvening,
    };
  });

  const evening = scored
    .filter((s) => s.isEvening)
    .sort((a, b) => b.hours - a.hours || b.inMin - a.inMin);
  if (evening.length > 0) {
    return evening[0].row;
  }

  return scored.sort((a, b) => b.hours - a.hours || b.inMin - a.inMin)[0].row;
}

function visitMatchesChartDay(
  row: ReporteRow,
  dateKey: string,
  realCheckRecords: ReporteChequeo[],
  payrollBranchMeta?: Record<number, BranchReportMeta>
): boolean {
  const meta = branchMetaForRow(row, payrollBranchMeta);
  if (!meta.nightScheduleEnabled) {
    return (
      getReportDateKey(row.checkIn!.assistanceCheck_checkDate) === dateKey
    );
  }
  return (
    operationalDayKeyForVisitRow(row, payrollBranchMeta, realCheckRecords) ===
    dateKey
  );
}

export type ChartDayCheckPair = {
  checkIn: { checkTime: string; checkDate?: string };
  checkOut: { checkTime: string; checkDate?: string };
};

function pickPrimaryCheckPairFromRows(
  rows: ReporteRow[],
  payrollBranchMeta?: Record<number, BranchReportMeta>
): ChartDayCheckPair | null {
  if (rows.length === 0) {
    return null;
  }

  const branchHasNight = rows.some(
    (row) => branchMetaForRow(row, payrollBranchMeta).nightScheduleEnabled
  );
  const primary = branchHasNight
    ? pickPrimaryNightVisitRow(rows)
    : rows.sort((a, b) =>
        String(a.checkIn!.assistanceCheck_checkTime).localeCompare(
          String(b.checkIn!.assistanceCheck_checkTime)
        )
      )[0];

  if (!primary?.checkIn || !primary.checkOut) {
    return null;
  }

  return {
    checkIn: {
      checkTime: primary.checkIn.assistanceCheck_checkTime,
      checkDate: primary.checkIn.assistanceCheck_checkDate
        ? String(primary.checkIn.assistanceCheck_checkDate)
        : undefined,
    },
    checkOut: {
      checkTime: primary.checkOut.assistanceCheck_checkTime,
      checkDate: primary.checkOut.assistanceCheck_checkDate
        ? String(primary.checkOut.assistanceCheck_checkDate)
        : undefined,
    },
  };
}

/** Una sola pasada sobre checadas del periodo (evita N× formatDataForReport en la tabla). */
export function buildCheckPairsByChartFecha(
  partnerId: number,
  realCheckRecords: ReporteChequeo[],
  periodStart: string,
  periodEnd: string,
  payrollBranchMeta?: Record<number, BranchReportMeta>
): Map<string, ChartDayCheckPair> {
  const byFecha = new Map<string, ChartDayCheckPair>();
  if (!realCheckRecords?.length || !partnerId) {
    return byFecha;
  }

  const rows = formatDataForReport(
    realCheckRecords,
    periodStart,
    periodEnd,
    payrollBranchMeta
  ).filter(
    (row) =>
      Number(row.checkIn?.partner_id) === partnerId &&
      row.checkIn &&
      row.checkOut
  );

  const byDateKey = new Map<string, ReporteRow[]>();
  for (const row of rows) {
    const meta = branchMetaForRow(row, payrollBranchMeta);
    const dateKey = meta.nightScheduleEnabled
      ? operationalDayKeyForVisitRow(row, payrollBranchMeta, realCheckRecords)
      : getReportDateKey(row.checkIn!.assistanceCheck_checkDate);

    if (!byDateKey.has(dateKey)) {
      byDateKey.set(dateKey, []);
    }
    byDateKey.get(dateKey)!.push(row);
  }

  for (const [dateKey, dayRows] of byDateKey) {
    const pair = pickPrimaryCheckPairFromRows(dayRows, payrollBranchMeta);
    if (!pair) continue;
    const fecha = moment.utc(dateKey, "YYYY-MM-DD").format("DD-MM-YYYY");
    byFecha.set(fecha, pair);
  }

  return byFecha;
}

function findCheckPairForChartDay(
  fecha: string,
  partnerId: number,
  realCheckRecords: ReporteChequeo[],
  periodStart: string,
  periodEnd: string,
  payrollBranchMeta?: Record<number, BranchReportMeta>
) {
  const dateKey = moment(fecha, "DD-MM-YYYY", true).isValid()
    ? moment(fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
    : moment.utc(fecha).format("YYYY-MM-DD");

  const rows = formatDataForReport(
    realCheckRecords,
    periodStart,
    periodEnd,
    payrollBranchMeta
  ).filter(
    (row) =>
      Number(row.checkIn?.partner_id) === partnerId &&
      row.checkIn &&
      row.checkOut &&
      visitMatchesChartDay(row, dateKey, realCheckRecords, payrollBranchMeta)
  );

  return pickPrimaryCheckPairFromRows(rows, payrollBranchMeta);
}

export function buildChartDetailedDisplayByFecha<
  T extends ChartDetailedDisplayRecord,
>(
  records: T[],
  options: {
    /** Preferir branchId + payrollBranchMeta (sucursal + turno Nocturno del colaborador). */
    nightScheduleEnabled?: boolean;
    branchId?: number;
    authorizedSchedule?: IAuthorizedHours[];
    realCheckRecords?: ReporteChequeo[];
    partnerId?: number;
    periodStart?: string;
    periodEnd?: string;
    payrollBranchMeta?: Record<number, BranchReportMeta>;
  }
): Map<string, T & { salidaRegistradaFecha?: string }> {
  const partnerNightEnabled = resolvePayrollPartnerNightScheduleEnabled({
    partnerId: options.partnerId,
    branchId: options.branchId,
    realCheckRecords: options.realCheckRecords,
    payrollBranchMeta: options.payrollBranchMeta,
  });

  const checkPairsByFecha =
    partnerNightEnabled &&
    options.realCheckRecords?.length &&
    options.partnerId &&
    options.periodStart &&
    options.periodEnd
      ? buildCheckPairsByChartFecha(
          options.partnerId,
          options.realCheckRecords,
          options.periodStart,
          options.periodEnd,
          options.payrollBranchMeta
        )
      : null;

  const map = new Map<string, T & { salidaRegistradaFecha?: string }>();
  for (const record of records) {
    map.set(
      record.fecha,
      applyChartDetailedNightDisplay(record, {
        ...options,
        precomputedCheckPair: checkPairsByFecha?.get(record.fecha) ?? null,
      })
    );
  }
  return map;
}

export function applyChartDetailedNightDisplay<
  T extends ChartDetailedDisplayRecord,
>(
  record: T,
  options: {
    nightScheduleEnabled?: boolean;
    branchId?: number;
    authorizedSchedule?: IAuthorizedHours[];
    realCheckRecords?: ReporteChequeo[];
    partnerId?: number;
    periodStart?: string;
    periodEnd?: string;
    payrollBranchMeta?: Record<number, BranchReportMeta>;
    precomputedCheckPair?: ChartDayCheckPair | null;
  }
): T & { salidaRegistradaFecha?: string } {
  const partnerNightEnabled = resolvePayrollPartnerNightScheduleEnabled({
    partnerId: options.partnerId,
    branchId: options.branchId,
    realCheckRecords: options.realCheckRecords,
    payrollBranchMeta: options.payrollBranchMeta,
  });

  if (!partnerNightEnabled) {
    return record;
  }

  if (
    record.entradaRegistrada === "N/A" &&
    record.salidaRegistrada === "N/A"
  ) {
    return record;
  }

  let next: T & { salidaRegistradaFecha?: string } = { ...record };

  const daySchedules = (options.authorizedSchedule || []).filter((s) => {
    const dateKey = moment(record.fecha, "DD-MM-YYYY", true).isValid()
      ? moment(record.fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
      : moment.utc(record.fecha).format("YYYY-MM-DD");
    return moment(s.exactDate).format("YYYY-MM-DD") === dateKey;
  });
  const dayVisits = dedupePayrollVisitsByPair(record.visits || []);

  if (
    record.entradaRegistrada === "N/A" ||
    !record.entradaRegistrada ||
    !record.salidaRegistrada ||
    record.salidaRegistrada === "N/A"
  ) {
    return next;
  }

  const pair =
    options.precomputedCheckPair !== undefined
      ? options.precomputedCheckPair
      : options.realCheckRecords?.length &&
          options.partnerId &&
          options.periodStart &&
          options.periodEnd
        ? findCheckPairForChartDay(
            record.fecha,
            options.partnerId,
            options.realCheckRecords,
            options.periodStart,
            options.periodEnd,
            options.payrollBranchMeta
          )
        : null;

  const rawIn = pair?.checkIn ?? { checkTime: record.entradaRegistrada };
  const rawOut = pair?.checkOut ?? { checkTime: record.salidaRegistrada };
  const { checkIn, checkOut } = normalizeRealChecksForDisplay(
    rawIn,
    rawOut,
    true
  );

  next.entradaRegistrada = checkIn?.checkTime ?? record.entradaRegistrada;
  next.salidaRegistrada = checkOut?.checkTime ?? record.salidaRegistrada;

  if (
    checkIn?.checkDate &&
    checkOut?.checkDate &&
    String(checkIn.checkDate) !== String(checkOut.checkDate)
  ) {
    next.salidaRegistradaFecha = moment
      .utc(checkOut.checkDate)
      .format("YYYY-MM-DD");
  } else {
    delete next.salidaRegistradaFecha;
  }

  const programado = resolveProgramadoDisplayForPayrollDay(daySchedules, dayVisits, {
    nightScheduleEnabled: partnerNightEnabled,
  });
  const schedule = findScheduleForFecha(
    record.fecha,
    options.authorizedSchedule
  );

  if (programado.usesRegisteredAsProgramado || isUnscheduledPayrollDay(daySchedules, dayVisits)) {
    next.entradaProgramada = appendSeconds(next.entradaRegistrada);
    next.salidaProgramada = appendSeconds(next.salidaRegistrada);
    const horas = resolvePayrollDayRegisteredHours(dayVisits, {
      entradaRegistrada: next.entradaRegistrada,
      salidaRegistrada: next.salidaRegistrada,
      nightScheduleEnabled: partnerNightEnabled,
    });
    next.horasRegistradas = horas;
    next.horasTrabajadasEnHorario = horas;
    next.horasExtras = 0;
  } else if (
    schedule &&
    record.entradaProgramada !== "Varios" &&
    record.entradaProgramada !== "N/A"
  ) {
    const scheduled = resolveScheduledTimesForDisplay(
      schedule.start,
      schedule.end,
      true
    );
    next.entradaProgramada = appendSeconds(scheduled.entrada);
    next.salidaProgramada =
      scheduled.salida === "00:00" || scheduled.salida === "00:00:00"
        ? "24:00:00"
        : appendSeconds(scheduled.salida);
  }

  return next;
}
